/**
 * pages/VerifyEmailPage.tsx
 * Route: /verify-email?token=xxx
 * Calls backend which verifies and redirects to /dashboard?verified=1
 */
import React, { useEffect, useState } from 'react';
import { Check, XCircle, Loader } from 'lucide-react';

const C = {
  amber100:'#FFF4E0', amber300:'#F5A623', amber400:'#C4811A',
  ink:'#1A1814', muted:'#7A7570', border:'#E8E4DC', surface:'#FAFAF8',
  white:'#FFFFFF', green:'#16A34A', red:'#DC2626',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

const VerifyEmailPage: React.FC = () => {
  const token  = new URLSearchParams(window.location.search).get('token');
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  const [status,  setStatus]  = useState<'loading'|'success'|'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token found in this link.');
      return;
    }

    // Use no-redirect fetch so we handle the response ourselves
    fetch(`${apiUrl}/auth/verify-email?token=${token}`, {
      method: 'GET',
      redirect: 'manual', // don't follow redirects automatically
    })
      .then(res => {
        // Status 0 = opaque redirect (means backend redirected = success)
        // Status 200 or 3xx = also success
        if (res.status === 0 || res.ok || res.status === 302 || res.status === 301) {
          setStatus('success');
          // Redirect to dashboard after short delay
          setTimeout(() => {
            window.location.replace('/dashboard?verified=1');
          }, 2000);
        } else {
          return res.json().then(d => {
            throw new Error(d.message || 'Verification failed');
          });
        }
      })
      .catch(err => {
        // If it's a network/CORS error on redirect, it still means success
        // because the backend only redirects after successful verification
        if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
          setStatus('success');
          setTimeout(() => {
            window.location.replace('/dashboard?verified=1');
          }, 2000);
        } else {
          setStatus('error');
          setMessage(err.message || 'This verification link has expired or is invalid.');
        }
      });
  }, [token, apiUrl]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,700&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:'DM Sans',sans-serif;background:${C.surface};}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
      `}</style>
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:24, background:`linear-gradient(160deg,#FFFDF8 60%,#FFF6E0 100%)` }}>

        {/* Logo */}
        <div style={{ position:'absolute', top:24, left:24 }}>
          <a href="/" style={{ display:'flex', alignItems:'center', gap:8, textDecoration:'none' }}>
            <div style={{ width:32, height:32, borderRadius:10, background:`linear-gradient(145deg,#FFD88A,#C4811A)`, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="15" height="15" viewBox="0 0 180 180" fill="none">
                <g transform="translate(90,88) rotate(-35)"><rect x="-12" y="-38" width="24" height="58" rx="7" fill="white"/><rect x="-12" y="-38" width="24" height="16" rx="7" fill="#F08030" opacity="0.9"/><polygon points="-12,20 12,20 0,44" fill="white" opacity="0.9"/></g>
                <circle cx="104" cy="102" r="42" fill="none" stroke="white" strokeWidth="13" opacity="0.95"/>
                <g transform="translate(126,124) rotate(45)"><rect x="-6" y="-4" width="30" height="10" rx="5" fill="white"/></g>
              </svg>
            </div>
            <span style={{ fontFamily:font.head, fontWeight:800, fontSize:20, letterSpacing:'-0.05em', color:C.ink }}>Jötr</span>
          </a>
        </div>

        <div style={{ maxWidth:400, width:'100%', background:C.white, borderRadius:20, padding:'40px 36px', boxShadow:'0 8px 32px rgba(0,0,0,0.06)', border:`1px solid ${C.border}`, textAlign:'center', animation:'fadeUp .4s ease' }}>

          {status === 'loading' && (
            <>
              <Loader size={40} color={C.amber300} style={{ animation:'spin .8s linear infinite', marginBottom:20 }}/>
              <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:22, color:C.ink, marginBottom:10 }}>Verifying your email…</h1>
              <p style={{ fontSize:14, color:C.muted, fontFamily:font.body }}>Just a moment please.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={{ width:64, height:64, borderRadius:'50%', background:'#F0FDF4', border:`2px solid ${C.green}44`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
                <Check size={30} color={C.green}/>
              </div>
              <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:24, color:C.ink, marginBottom:10 }}>
                Email verified! 🎉
              </h1>
              <p style={{ fontSize:14, color:C.muted, fontFamily:font.body, lineHeight:1.7, marginBottom:24 }}>
                Your email has been verified successfully. Taking you to your notes…
              </p>
              <div style={{ width:'100%', height:4, borderRadius:2, background:`${C.border}`, overflow:'hidden' }}>
                <div style={{ height:'100%', background:`linear-gradient(90deg,${C.amber300},${C.amber400})`, animation:'progress 2s linear forwards' }}/>
              </div>
              <style>{`@keyframes progress{from{width:0}to{width:100%}}`}</style>
            </>
          )}

          {status === 'error' && (
            <>
              <div style={{ width:64, height:64, borderRadius:'50%', background:'#FEF2F2', border:`2px solid ${C.red}44`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
                <XCircle size={30} color={C.red}/>
              </div>
              <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:22, color:C.ink, marginBottom:10 }}>Link expired</h1>
              <p style={{ fontSize:14, color:C.muted, fontFamily:font.body, lineHeight:1.7, marginBottom:24 }}>
                {message || 'This verification link has expired or already been used. Request a new one from your dashboard.'}
              </p>
              <a href="/dashboard" style={{ display:'inline-flex', padding:'12px 28px', borderRadius:11, background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:15, textDecoration:'none', fontFamily:font.body }}>
                Go to dashboard
              </a>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default VerifyEmailPage;